import { viewerApi } from '@viewer/lib/viewerApi'
import { rawIfc } from '@viewer/lib/rawIfc'

export interface ModelGraphNode {
  id: string
  modelId: string
  localId: number
  ifcClass: string
  globalId: string | null
  name: string | null
  text: string
  neighbours: string[]
  /** Tem um tipo associado (IfcRelDefinesByType). */
  hasType: boolean
  /** Tem material associado (IfcRelAssociatesMaterial). */
  hasMaterial: boolean
  /** Tem alguma PROPRIEDADE cujo nome fala de estado, condição,
   *  reutilização, desmontagem ou vida útil. */
  hasConditionOrReuse: boolean
}

export interface ModelGraph {
  modelId: string
  builtAt: string
  nodes: ModelGraphNode[]
  edges: number
  /** De onde vieram os factos de tipo e material. O Fragments não expõe
   *  o IfcRelDefinesByType nem o IfcRelAssociatesMaterial nos dados de
   *  cada item, por isso num modelo sem IFC por trás essas duas colunas
   *  não são observáveis e o painel tem de o dizer, em vez de mostrar
   *  zero como se fosse uma contagem. */
  typeAndMaterialFrom: 'ifc' | 'indisponivel'
}

export function modelValueText(
  value: unknown,
  seen = new WeakSet<object>(),
  depth = 0,
): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'object') {
    if (depth >= 8 || seen.has(value as object)) return ''
    seen.add(value as object)
    if ('value' in value) {
      return modelValueText(
        (value as { value?: unknown }).value,
        seen,
        depth + 1,
      )
    }
    return Object.values(value)
      .slice(0, 200)
      .map((child) => modelValueText(child, seen, depth + 1))
      .join(' ')
  }
  return String(value)
}

/** Nomes de propriedade que indicam informação útil para reutilização.
 *  Deliberadamente sobre os NOMES das propriedades e não sobre os seus
 *  valores: procurar a palavra "material" no meio dos valores dá com
 *  qualquer texto que por acaso a contenha, e falha em todos os
 *  modelos onde a informação existe mas o valor não repete a palavra. */
const REUSE_PROPERTY = /(condition|status|reuse|reusable|recycl|service\s*life|disassembl|demountab|circular)/i

/** As propriedades de um item têm nomes que valem por si. */
function propertyNames(data: unknown): string[] {
  if (!data || typeof data !== 'object') return []
  const defined = (data as Record<string, unknown>).IsDefinedBy
  if (!Array.isArray(defined)) return []
  const out: string[] = []
  for (const pset of defined) {
    if (!pset || typeof pset !== 'object') continue
    const psetName = attribute(pset, 'Name')
    if (psetName) out.push(psetName)
    const props = (pset as Record<string, unknown>).HasProperties
    if (!Array.isArray(props)) continue
    for (const prop of props) {
      const name = attribute(prop, 'Name')
      if (name) out.push(name)
    }
  }
  return out
}

function attribute(data: unknown, key: string): string | null {
  if (!data || typeof data !== 'object') return null
  const entry = Object.entries(data as Record<string, unknown>).find(
    ([name]) => name.toLowerCase() === key.toLowerCase(),
  )?.[1]
  const value = modelValueText(entry).trim()
  return value || null
}

/** Elementos abrangidos por uma relação IFC, pelo seu expressID.
 *
 *  O expressID do web-ifc é o mesmo número que o Fragments usa como
 *  localId, por isso os dois lados falam do mesmo elemento. */
function relatedObjectIds(modelId: string, relationType: string): Set<number> {
  const out = new Set<number>()
  for (const relId of rawIfc.getLineIDsByType(modelId, relationType)) {
    const rel = rawIfc.getEntity(modelId, relId) as Record<string, unknown> | null
    const related = rel?.RelatedObjects
    if (!Array.isArray(related)) continue
    for (const ref of related) {
      // O `getEntity` devolve as relações já resolvidas (flatten), por
      // isso cada elemento vem como objecto com `expressID`, e não como
      // uma referência `{ value: 123 }`. Ler só o `value` dava sempre
      // conjunto vazio, e o painel dizia que nenhum elemento tinha tipo
      // num modelo com 170 relações de tipo.
      const entry = ref as { expressID?: unknown; value?: unknown }
      if (typeof entry?.expressID === 'number') out.add(entry.expressID)
      else if (typeof entry?.value === 'number') out.add(entry.value)
    }
  }
  return out
}

export async function buildModelGraph(modelId: string): Promise<ModelGraph> {
  const model = viewerApi.getModel(modelId)
  if (!model) throw new Error('O modelo já não está carregado.')
  const categories = await model.getItemsOfCategories([/.*/])
  const nodes = new Map<number, ModelGraphNode>()

  // Tipo e material leem-se do IFC, que é onde estão. Tentar deduzi-los
  // dos dados do Fragments dava zero num modelo com 170 relações de
  // tipo declaradas: o Fragments simplesmente não as expõe aqui.
  let tipados = new Set<number>()
  let comMaterial = new Set<number>()
  let typeAndMaterialFrom: 'ifc' | 'indisponivel' = 'indisponivel'
  if (rawIfc.hasCachedBytes(modelId)) {
    try {
      if (!rawIfc.hasModel(modelId)) await rawIfc.loadModel(modelId)
      tipados = relatedObjectIds(modelId, 'IFCRELDEFINESBYTYPE')
      comMaterial = relatedObjectIds(modelId, 'IFCRELASSOCIATESMATERIAL')
      typeAndMaterialFrom = 'ifc'
    } catch {
      // Fica 'indisponivel': o painel diz que não sabe, em vez de dizer zero.
    }
  }
  for (const [ifcClass, ids] of Object.entries(categories)) {
    for (let offset = 0; offset < (ids?.length ?? 0); offset += 250) {
      const batch = ids!.slice(offset, offset + 250)
      const rows = await model.getItemsData(batch, {
        attributesDefault: true,
        relations: {
          // `relations: true` traz as propriedades dentro de cada
          // conjunto: sem elas só se sabe que existe um Pset, não o que
          // ele diz.
          IsDefinedBy: { attributes: true, relations: true },
          IsTypedBy: { attributes: true, relations: false },
          HasAssociations: { attributes: true, relations: false },
        },
        relationsDefault: { attributes: false, relations: false },
      })
      batch.forEach((localId, index) => {
        const data = rows[index] ?? null
        const nomes = propertyNames(data)
        nodes.set(localId, {
          id: `${modelId}:${localId}`,
          modelId,
          localId,
          ifcClass: ifcClass.toUpperCase(),
          // O Fragments expõe o GlobalId como `_guid`. Procurar por
          // "GlobalId" devolvia null para todos os elementos, e o
          // índice dizia que nenhum tinha identificador, num modelo em
          // que todos têm.
          globalId: attribute(data, '_guid') ?? attribute(data, 'GlobalId'),
          name: attribute(data, 'Name'),
          // O texto serve a procura; os factos abaixo servem a contagem.
          text: [modelValueText(data), ...nomes].join(' '),
          neighbours: [],
          hasType: tipados.has(localId),
          hasMaterial: comMaterial.has(localId),
          hasConditionOrReuse: nomes.some((name) => REUSE_PROPERTY.test(name)),
        })
      })
    }
  }

  let edges = 0
  try {
    const tree = await model.getSpatialStructure()
    const walk = (item: typeof tree, parent: number | null) => {
      const localId = item.localId ?? null
      if (localId !== null && parent !== null) {
        const child = nodes.get(localId)
        const parentNode = nodes.get(parent)
        if (child && parentNode) {
          child.neighbours.push(parentNode.id)
          parentNode.neighbours.push(child.id)
          edges += 1
        }
      }
      const nextParent = localId ?? parent
      item.children?.forEach((child) => walk(child, nextParent))
    }
    walk(tree, null)
  } catch {
    // A flat graph still supports fact retrieval when no spatial tree exists.
  }

  return {
    modelId,
    builtAt: new Date().toISOString(),
    nodes: [...nodes.values()],
    edges,
    typeAndMaterialFrom,
  }
}

export function queryModelGraph(
  graph: ModelGraph,
  query: string,
  limit = 50,
): ModelGraphNode[] {
  const tokens = query.toLowerCase().split(/\s+/).filter(Boolean)
  if (tokens.length === 0) return graph.nodes.slice(0, limit)
  const byId = new Map(graph.nodes.map((node) => [node.id, node]))
  const scored = graph.nodes
    .map((node) => {
      const haystack = `${node.ifcClass} ${node.globalId ?? ''} ${node.name ?? ''} ${node.text}`.toLowerCase()
      const score = tokens.reduce((sum, token) => sum + (haystack.includes(token) ? 1 : 0), 0)
      return { node, score }
    })
    .filter((row) => row.score > 0)
    .sort((a, b) => b.score - a.score || a.node.ifcClass.localeCompare(b.node.ifcClass))
  const out: ModelGraphNode[] = []
  const seen = new Set<string>()
  for (const { node } of scored) {
    for (const candidate of [node, ...node.neighbours.map((id) => byId.get(id)).filter((value): value is ModelGraphNode => Boolean(value))]) {
      if (seen.has(candidate.id)) continue
      seen.add(candidate.id)
      out.push(candidate)
      if (out.length >= limit) return out
    }
  }
  return out
}

export interface CircularityAssessment {
  /** Entidades com identidade própria (com GlobalId). É sobre estas que
   *  faz sentido perguntar se estão documentadas. */
  total: number
  /** Deixados de fora: unidades, valores de propriedade e outras
   *  entidades que a norma não obriga a identificar. Contá-las como
   *  elementos por documentar baixava o índice sem razão. */
  ignored: number
  withGlobalId: number
  withName: number
  withType: number
  withMaterial: number
  withConditionOrReuseData: number
  documentationScore: number
}

/** Conta o que ESTA declarado no modelo, elemento a elemento.
 *
 *  A primeira versao contava ocorrencias de palavras como "material" no
 *  meio dos VALORES de cada elemento. Isso nao media nada: dava zero num
 *  modelo com materiais atribuidos, e daria um falso positivo em
 *  qualquer elemento cujo nome contivesse a palavra. Cada campo aqui
 *  corresponde agora a uma relacao IFC concreta, lida na construcao do
 *  grafo. */
export function assessCircularity(nodes: ModelGraphNode[]): CircularityAssessment {
  // Só entidades com GlobalId. Um IfcSIUnit ou um IfcPropertySingleValue
  // não é um elemento por documentar: incluí-los punha 688 entradas no
  // denominador de um modelo com 917 elementos.
  const rooted = nodes.filter((node) => Boolean(node.globalId))
  const total = rooted.length
  const withGlobalId = total
  const withName = rooted.filter((node) => Boolean(node.name)).length
  const withType = rooted.filter((node) => node.hasType).length
  const withMaterial = rooted.filter((node) => node.hasMaterial).length
  const withConditionOrReuseData = rooted.filter(
    (node) => node.hasConditionOrReuse,
  ).length
  // O GlobalId define a população, por isso não entra na pontuação:
  // seria uma coluna sempre a 100%.
  const denominator = Math.max(total * 4, 1)
  return {
    total,
    ignored: nodes.length - total,
    withGlobalId,
    withName,
    withType,
    withMaterial,
    withConditionOrReuseData,
    documentationScore: Math.round(
      ((withName + withType + withMaterial + withConditionOrReuseData) /
        denominator) *
        100,
    ),
  }
}
